import {
  CalendarOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  EditOutlined,
  EnvironmentOutlined,
  PercentageOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Collapse,
  Progress,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import React from "react";
import { Link } from "../links/Link";
import { SocialTypeId } from "../social/Social.model";
import { Company } from "./Company";
import { CompanyHelper } from "./Company.Helper";
import { CompanyImage } from "./Company.Image";

const { Text, Title } = Typography;

interface CompanyListItemProps {
  company: Company;
  showAdminActions?: boolean;
}

export function CompanyListItem(props: CompanyListItemProps) {
  let company = props.company;
  const isArchived = !!company.archivedAt;

  const hasLinkedIn = !!company.socialLinks?.find(
    (sl) => sl.type.id === SocialTypeId.LINKEDIN,
  );
  const hasBanner = !!company.banner;
  const hasAbout = !!company.dd;
  const hasIndustry = !!company.industry;
  const hasSize = !!company.companySize;
  const hasHeadquarters = !!company.headquarters;
  const hasSpecialties = !!company.specialties;
  const hasFoundedYear = !!company.foundedYear;
  const hasKeywords = !!(company.keywords && company.keywords.length > 0);
  const hasSocialLinks = !!(
    company.socialLinks && company.socialLinks.length > 0
  );

  // Calculate data completeness score (out of 10 key fields)
  const completeFields = [
    hasIndustry,
    hasSize,
    hasBanner,
    hasAbout,
    hasLinkedIn,
    hasKeywords,
    hasHeadquarters,
    hasSpecialties,
    hasFoundedYear,
    hasSocialLinks,
  ].filter(Boolean).length;
  const completenessPercent = Math.round((completeFields / 10) * 100);

  // Determine completeness level
  const getCompletenessColor = (percent: number) => {
    if (percent >= 80) return "green";
    if (percent >= 60) return "gold";
    if (percent >= 40) return "orange";
    return "red";
  };

  const StatusIcon = ({ present }: { present: boolean }) =>
    present ? (
      <CheckCircleFilled className="text-success" />
    ) : (
      <CloseCircleFilled className="text-danger" />
    );

  return (
    <Card
      hoverable={!isArchived}
      className={`CompanyListItem mb-3 border-0 shadow-sm ${isArchived ? "bg-light" : ""}`}
      bodyStyle={{ padding: "16px" }}
      style={{
        opacity: isArchived ? 0.7 : 1,
        borderRadius: "12px",
        borderLeft:
          completenessPercent < 60 ? "4px solid #ff4d4f" : "4px solid #52c41a",
        filter: isArchived ? "grayscale(0.4)" : "none",
        transition: "all 0.3s ease",
      }}
    >
      <Row gutter={[16, 16]} align="middle">
        {/* Logo */}
        <Col xs={4} sm={3} md={2} className="d-flex justify-content-center">
          <div
            className="p-1 border rounded-3 bg-white shadow-sm d-flex align-items-center justify-content-center"
            style={{ width: "50px", height: "50px", flexShrink: 0 }}
          >
            <CompanyImage width={40} company={company} />
          </div>
        </Col>

        {/* Company Name & Quick Info */}
        <Col xs={20} sm={12} md={8}>
          <div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Title level={5} className="mb-0">
                <Link
                  href={CompanyHelper.getViewUrl(company)}
                  className="text-dark text-decoration-none"
                  target="_blank"
                >
                  {company.dl}
                </Link>
              </Title>
              {isArchived && (
                <Tag color="error" className="m-0 border-0 fw-bold">
                  ARCHIVED
                </Tag>
              )}
            </div>

            {company.desDl && company.desDl !== company.dl && (
              <Text type="secondary" className="d-block small mb-1">
                {company.desDl}
              </Text>
            )}

            <Space size={[4, 4]} wrap style={{ marginTop: "4px" }}>
              {hasIndustry ? (
                <Tag color="blue" className="border-0 small m-0">
                  {company.industry}
                </Tag>
              ) : (
                <Tooltip title="Missing Industry">
                  <Tag color="error" className="border-0 small m-0">
                    <WarningOutlined className="me-1" />
                    No Industry
                  </Tag>
                </Tooltip>
              )}
              {hasSize ? (
                <Tag color="cyan" className="border-0 small m-0">
                  {company.companySize}
                </Tag>
              ) : (
                <Tooltip title="Missing Size">
                  <Tag color="error" className="border-0 small m-0">
                    <WarningOutlined className="me-1" />
                    No Size
                  </Tag>
                </Tooltip>
              )}
            </Space>
          </div>
        </Col>

        {/* Completeness Score */}
        <Col
          xs={24}
          sm={9}
          md={5}
          className={props.showAdminActions ? "" : "d-none"}
        >
          <div className="p-2 rounded-3" style={{ backgroundColor: "#fafafa" }}>
            <div className="d-flex align-items-center justify-content-between mb-1">
              <Text className="small fw-bold">Data Completeness</Text>
              <Text
                className="small fw-bold"
                style={{
                  color:
                    completenessPercent >= 80
                      ? "#52c41a"
                      : completenessPercent >= 60
                        ? "#faad14"
                        : "#ff4d4f",
                }}
              >
                {completenessPercent}%
              </Text>
            </div>
            <Progress
              percent={completenessPercent}
              strokeColor={getCompletenessColor(completenessPercent)}
              size="small"
              showInfo={false}
            />
            <Space wrap size={[2, 2]} style={{ marginTop: "6px" }}>
              <Tooltip
                title={hasIndustry ? "✓ Industry" : "✗ Missing Industry"}
              >
                <StatusIcon present={hasIndustry} />
              </Tooltip>
              <Tooltip title={hasSize ? "✓ Size" : "✗ Missing Size"}>
                <StatusIcon present={hasSize} />
              </Tooltip>
              <Tooltip
                title={hasIndustry ? "✓ Industry" : "✗ Missing Industry"}
              >
                <StatusIcon present={hasBanner} />
              </Tooltip>
              <Tooltip title={hasAbout ? "✓ About" : "✗ Missing About"}>
                <StatusIcon present={hasAbout} />
              </Tooltip>
              <Tooltip
                title={hasLinkedIn ? "✓ LinkedIn" : "✗ Missing LinkedIn"}
              >
                <StatusIcon present={hasLinkedIn} />
              </Tooltip>
              <Tooltip
                title={hasKeywords ? "✓ Keywords" : "✗ Missing Keywords"}
              >
                <StatusIcon present={hasKeywords} />
              </Tooltip>
            </Space>
          </div>
        </Col>

        {/* Missing Fields Alert */}
        <Col xs={24} sm={3} md={3}>
          {completenessPercent < 60 ? (
            <Tooltip title={`${10 - completeFields} fields missing`}>
              <div className="d-flex gap-2">
                <Button
                  type="primary"
                  danger
                  size="small"
                  icon={<WarningOutlined />}
                  href={CompanyHelper.getEditUrl(company)}
                  target="_blank"
                  style={{ flexGrow: 1 }}
                >
                  Complete
                </Button>
              </div>
            </Tooltip>
          ) : (
            <Button
              type="primary"
              ghost
              size="small"
              icon={<EditOutlined />}
              href={CompanyHelper.getEditUrl(company)}
              target="_blank"
              style={{ width: "100%" }}
            >
              Edit
            </Button>
          )}
        </Col>

        {/* Missing Fields Details (Admin) */}
        {props.showAdminActions && (
          <Col xs={24}>
            <Collapse
              size="small"
              items={[
                {
                  key: "details",
                  label: (
                    <Space size="small">
                      <PercentageOutlined style={{ fontSize: "12px" }} />
                      <Text strong className="small">
                        {completeFields}/{10} fields completed
                      </Text>
                    </Space>
                  ),
                  children: (
                    <div>
                      <Row gutter={[12, 12]}>
                        {/* Required Fields Status */}
                        <Col xs={24} sm={12}>
                          <Text strong className="small d-block mb-2">
                            Required Fields
                          </Text>
                          <Space
                            direction="vertical"
                            size="small"
                            style={{ width: "100%" }}
                          >
                            <div className="d-flex align-items-center justify-content-between">
                              <Text className="small">Industry</Text>
                              <StatusIcon present={hasIndustry} />
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                              <Text className="small">Company Size</Text>
                              <StatusIcon present={hasSize} />
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                              <Text className="small">
                                <EnvironmentOutlined className="me-2" />
                                Headquarters
                              </Text>
                              <StatusIcon present={hasHeadquarters} />
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                              <Text className="small">Specialties</Text>
                              <StatusIcon present={hasSpecialties} />
                            </div>
                          </Space>
                        </Col>

                        {/* Content Fields Status */}
                        <Col xs={24} sm={12}>
                          <Text strong className="small d-block mb-2">
                            Content & Engagement
                          </Text>
                          <Space
                            direction="vertical"
                            size="small"
                            style={{ width: "100%" }}
                          >
                            <div className="d-flex align-items-center justify-content-between">
                              <Text className="small">About Section</Text>
                              <StatusIcon present={hasAbout} />
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                              <Text className="small">Banner Image</Text>
                              <StatusIcon present={hasBanner} />
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                              <Text className="small">Keywords</Text>
                              <StatusIcon present={hasKeywords} />
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                              <Text className="small">Social Links</Text>
                              <StatusIcon present={hasSocialLinks} />
                            </div>
                          </Space>
                        </Col>

                        {/* Other Details */}
                        <Col xs={24}>
                          <Text strong className="small d-block mb-2">
                            Additional Info
                          </Text>
                          <Space
                            direction="vertical"
                            size="small"
                            style={{ width: "100%" }}
                          >
                            {company.foundedYear && (
                              <div className="d-flex align-items-center justify-content-between">
                                <Text className="small">
                                  <CalendarOutlined className="me-2" />
                                  Founded
                                </Text>
                                <Text className="small">
                                  {company.foundedYear}
                                </Text>
                              </div>
                            )}
                            {company.tl && (
                              <div className="d-flex align-items-center justify-content-between">
                                <Text className="small">Title</Text>
                                <Text className="small">{company.tl}</Text>
                              </div>
                            )}
                            <Tooltip title={company.id}>
                              <div className="d-flex align-items-center justify-content-between">
                                <Text className="small">ID</Text>
                                <Text copyable className="small">
                                  {company.id.substring(0, 8)}...
                                </Text>
                              </div>
                            </Tooltip>
                          </Space>
                        </Col>
                      </Row>
                    </div>
                  ),
                },
              ]}
            />
          </Col>
        )}
      </Row>
    </Card>
  );
}
